import {ErrorMessage} from 'formik'
import {FC, useState} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {FormErrorMessage} from '@/components/common/form/FormErrorMessage'
import {FormItem} from '@/components/common/form/FormItem'
import {FormSection} from '@/components/common/form/FormSection'
import {TipsModal} from '@/components/common/tips/TipsModal'
import {TextArea} from '@/components/inputs/TextArea'
import {TextInput} from '@/components/inputs/TextInput'
import {useCommonContent} from '@/content/common'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {Trans, useTranslation} from '@/i18n'
import {FORM_TEXTAREA_MAX_HEIGHT} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {analytics} from '@/utils/tracking/analytics'

type BusinessInfoFormProps = {
  isAnimated: boolean
  animationStarted: boolean
  hasWebsiteError: boolean
  newDescriptionKey?: string
  showsWebsite?: boolean
}

export const BusinessInfoForm: FC<BusinessInfoFormProps> = ({
  isAnimated,
  animationStarted,
  hasWebsiteError,
  newDescriptionKey,
  showsWebsite = true,
}) => {
  const {t} = useTranslation()
  const [isTipsModalVisible, setIsTipsModalVisible] = useState(false)

  const {title, description, gsLabel, gsPlaceholder, websiteLabel, websiteExamples, gsTips} =
    useTrademarkVerificationContent().serviceDescription
  const {websiteUrlPlaceholder} = useCommonContent()

  return (
    <>
      <FormSection
        title={t(title)}
        description={
          <Text variant={TextVariant.text14}>
            <Trans i18nKey={newDescriptionKey || description}>
              text
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setIsTipsModalVisible(true)
                  analytics.track('Tips for good G&S description clicked')
                }}
              >
                text
              </button>
            </Trans>
          </Text>
        }
        isAnimated={isAnimated}
        animationStarted={animationStarted}
      >
        <FormItem label={t(gsLabel)}>
          <ErrorMessage
            name="client_business_description"
            render={(msg) => <FormErrorMessage message={msg} />}
          />
          <TextArea
            name="client_business_description"
            placeholder={t(gsPlaceholder)}
            numberOfRows={2}
            data-test="businessDescription"
            autosize
            maxHeight={FORM_TEXTAREA_MAX_HEIGHT}
          />
        </FormItem>

        {showsWebsite && (
          <FormItem label={t(websiteLabel)}>
            <ErrorMessage
              name="client_website"
              render={(msg) => <FormErrorMessage message={msg} />}
            />
            <div>
              <TextInput
                name="client_website"
                data-test="client_website"
                hasError={hasWebsiteError}
                placeholder={t(websiteUrlPlaceholder)}
              />
              {!hasWebsiteError && (
                <div className="example">
                  <Text variant={TextVariant.caption} className="text-block">
                    {t(websiteExamples)}
                  </Text>
                </div>
              )}
            </div>
          </FormItem>
        )}
      </FormSection>
      <TipsModal
        isVisible={isTipsModalVisible}
        title={t(gsTips.title)}
        description={t(gsTips.description)}
        categories={gsTips.categories}
        closeModal={() => setIsTipsModalVisible(false)}
      />

      <style jsx>{`
        .underline {
          text-decoration: underline;
          color: ${theme.palette.info.main};
        }
        .example {
          margin-top: ${theme.spacing[1]};
        }
      `}</style>
    </>
  )
}
